/**
 * Europe/Brussels wall-clock helpers. The server runs in UTC, so a booking date can
 * never be derived from a local `Date` (`toISOString()` on an evening Brussels time
 * yields the previous day). Everything here goes through `Intl` with an explicit zone.
 */

/** 'YYYY-MM-DD' in Europe/Brussels. */
export type DateKey = string;

const DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Brussels',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const PARTS_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Brussels',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export function brusselsDateKey(instant: Date = new Date()): DateKey {
  return DATE_FMT.format(instant);
}

/** Brussels UTC offset in milliseconds at a given instant. */
function offsetMs(at: Date): number {
  const parts = Object.fromEntries(
    PARTS_FMT.formatToParts(at).map((p) => [p.type, p.value])
  ) as Record<string, string>;
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  return asUtc - at.getTime();
}

/**
 * The UTC instant of a Brussels wall clock. Solved in two passes so the DST
 * transitions resolve: the offset is sampled at the first guess, then re-sampled at
 * the corrected instant in case the guess landed on the other side of the switch.
 */
export function brusselsWallClockToUtc(date: DateKey, hhmm: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [h, min] = hhmm.split(':').map(Number);
  const naive = Date.UTC(y, m - 1, d, h, min, 0);
  let guess = new Date(naive - offsetMs(new Date(naive)));
  guess = new Date(naive - offsetMs(guess));
  return guess;
}

export function addDays(date: DateKey, days: number): DateKey {
  const [y, m, d] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
}

/** 0 = Sunday … 6 = Saturday. Calendar-only, so plain UTC arithmetic is exact. */
export function weekdayOf(date: DateKey): number {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function isBusinessDay(date: DateKey): boolean {
  const day = weekdayOf(date);
  return day >= 1 && day <= 5;
}

/** Interviews are held Tuesday to Friday only. */
export function isInterviewDay(date: DateKey): boolean {
  const day = weekdayOf(date);
  return day >= 2 && day <= 5;
}

/** Well-formed AND real: '2026-02-30' passes a regex but Graph rejects it. */
export function isValidDateKey(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [y, m, d] = date.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

/** Business days from `from` to `to`, inclusive. Both must be valid date keys. */
export function businessDaysBetween(
  from: DateKey,
  to: DateKey,
  isOpenDay: (date: DateKey) => boolean = isBusinessDay
): DateKey[] {
  const out: DateKey[] = [];
  for (let day = from; day <= to; day = addDays(day, 1)) {
    if (isOpenDay(day)) out.push(day);
  }
  return out;
}
